import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext/AuthContext'
import { auth } from '../firebase/config/config'

export type SubscriptionPlan = 'free' | 'standard' | 'premium' | 'custom'

export interface SubscriptionData {
  id: string
  userId: string
  plan: SubscriptionPlan
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  status: 'active' | 'canceled' | 'past_due' | 'incomplete' | 'trialing'
  currentPeriodStart: Date
  currentPeriodEnd: Date
  cancelAtPeriodEnd: boolean
  additionalMaps: number
  createdAt: Date
  updatedAt: Date
}

export interface BillingData {
  subscription: SubscriptionData | null
  monthlyUsage: number
  monthlyCreations: number
  totalMaps: number
  canCreateMap: boolean
}

export interface PlanLimits {
  maxMaps: number
  includedViews: number
  pricePerAdditionalMap: number
  pricePerHundredViews: number
}

export const PLAN_DETAILS: Record<
  SubscriptionPlan,
  { name: string; limits: PlanLimits; price: number }
> = {
  free: {
    name: 'Free Plan',
    limits: {
      maxMaps: 1,
      includedViews: 100,
      pricePerAdditionalMap: 100, // $1.00
      pricePerHundredViews: 0,
    },
    price: 0,
  },
  standard: {
    name: 'Standard Plan',
    limits: {
      maxMaps: 50,
      includedViews: 10000,
      pricePerAdditionalMap: 100, // $1.00
      pricePerHundredViews: 25, // $0.25
    },
    price: 2000, // $20.00
  },
  premium: {
    name: 'Premium Plan',
    limits: {
      maxMaps: 75,
      includedViews: 50000,
      pricePerAdditionalMap: 100, // $1.00
      pricePerHundredViews: 25, // $0.25
    },
    price: 5000, // $50.00
  },
  custom: {
    name: 'Custom Plan',
    limits: {
      maxMaps: -1, // unlimited
      includedViews: -1, // unlimited
      pricePerAdditionalMap: 0,
      pricePerHundredViews: 0,
    },
    price: 0, // Contact for pricing
  },
}

export const useBilling = () => {
  const { user } = useAuth()
  const [billingData, setBillingData] = useState<BillingData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchBillingData = async () => {
    if (!user) {
      setLoading(false)
      return
    }

    try {
      setError(null)

      // Check if user is authenticated
      if (!auth.currentUser) {
        console.error('No authenticated user found')
        setError('Please sign in to view billing information')
        return
      }

      const idToken = await auth.currentUser.getIdToken()
      if (!idToken) {
        console.error('Failed to get authentication token')
        setError('Authentication error - please sign in again')
        return
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'
      console.log('Fetching billing data from:', `${apiUrl}/subscription/status`)

      const response = await fetch(
        `${apiUrl}/subscription/status`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      )

      if (!response.ok) {
        console.error('Response not OK:', response.status, response.statusText)
        if (response.status === 401) {
          setError('Authentication failed - please sign in again')
          return
        }
      }

      const data = await response.json()
      console.log('Billing data response:', data)

      if (data.success && data.data) {
        setBillingData({
          subscription: data.data.subscription
            ? {
                ...data.data.subscription,
                currentPeriodStart: new Date(
                  data.data.subscription.currentPeriodStart,
                ),
                currentPeriodEnd: new Date(
                  data.data.subscription.currentPeriodEnd,
                ),
                createdAt: new Date(data.data.subscription.createdAt),
                updatedAt: new Date(data.data.subscription.updatedAt),
              }
            : null,
          monthlyUsage: data.data.monthlyUsage || 0,
          monthlyCreations: data.data.monthlyCreations || 0,
          totalMaps: data.data.totalMaps || 0,
          canCreateMap: data.data.canCreateMap || false,
        })
      } else {
        console.error('API returned error:', data.error)
        setError(data.error || 'Failed to fetch billing data')
      }
    } catch (err) {
      console.error('Error fetching billing data:', err)
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Cannot connect to server - please check if the backend is running')
      } else {
        setError(`Failed to fetch billing data: ${err instanceof Error ? err.message : 'Unknown error'}`)
      }
    } finally {
      setLoading(false)
    }
  }

  const createCheckoutSession = async (plan: 'standard' | 'premium') => {
    if (!user) throw new Error('User not authenticated')

    const idToken = await auth.currentUser?.getIdToken()
    if (!idToken) throw new Error('Authentication error')

    const response = await fetch(
      `${
        process.env.REACT_APP_API_URL || 'http://localhost:3001/api'
      }/subscription/checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ plan }),
      },
    )

    const data = await response.json()

    if (data.success && data.data.url) {
      window.location.href = data.data.url
    } else {
      throw new Error(data.error || 'Failed to create checkout session')
    }
  }

  const createPortalSession = async () => {
    if (!user) throw new Error('User not authenticated')

    const idToken = await auth.currentUser?.getIdToken()
    if (!idToken) throw new Error('Authentication error')

    const response = await fetch(
      `${
        process.env.REACT_APP_API_URL || 'http://localhost:3001/api'
      }/subscription/portal`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      },
    )

    const data = await response.json()

    if (data.success && data.data.url) {
      window.location.href = data.data.url
    } else {
      throw new Error(data.error || 'Failed to create portal session')
    }
  }

  useEffect(() => {
    fetchBillingData()
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  // Helper functions
  const getCurrentPlanDetails = () => {
    if (!billingData?.subscription) return PLAN_DETAILS.free
    return PLAN_DETAILS[billingData.subscription.plan]
  }

  const getUsagePercentage = (used: number, limit: number) => {
    if (limit === -1) return 0 // Unlimited
    return Math.min((used / limit) * 100, 100)
  }

  const isOverLimit = (used: number, limit: number) => {
    if (limit === -1) return false // Unlimited
    return used > limit
  }

  const getDaysUntilRenewal = () => {
    if (!billingData?.subscription) return null
    const now = new Date()
    const renewalDate = billingData.subscription.currentPeriodEnd
    const diffTime = renewalDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const getOverageCharges = () => {
    if (!billingData)
      return {
        mapOverage: 0,
        mapOverageCost: 0,
        viewOverage: 0,
        viewOverageCost: 0,
      }

    const planDetails = getCurrentPlanDetails()
    const { limits } = planDetails

    // Calculate map overage
    const totalMapsAllowed =
      limits.maxMaps + (billingData.subscription?.additionalMaps || 0)
    const mapOverage =
      limits.maxMaps === -1
        ? 0
        : Math.max(0, billingData.monthlyCreations - totalMapsAllowed)

    // Calculate view overage (in hundreds)
    const viewOverage = Math.max(
      0,
      billingData.monthlyUsage -
        (limits.includedViews === -1 ? 0 : limits.includedViews),
    )
    const viewOverageHundreds = Math.ceil(viewOverage / 100)

    return {
      mapOverage,
      mapOverageCost: mapOverage * limits.pricePerAdditionalMap,
      viewOverage: viewOverageHundreds,
      viewOverageCost: viewOverageHundreds * limits.pricePerHundredViews,
    }
  }

  return {
    billingData,
    loading,
    error,
    refetch: fetchBillingData,
    createCheckoutSession,
    createPortalSession,
    getCurrentPlanDetails,
    getUsagePercentage,
    isOverLimit,
    getDaysUntilRenewal,
    getOverageCharges,
  }
}
